/**
 * Login con estilo CatcSoft DS (línea A "Datadog refined"):
 * panel izquierdo dark con gradient radial + branding + dimensiones D1-D8 +
 * stats hero; form-side derecho con eyebrow + form + bloque VPN + attribution.
 */

import { useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { api } from "../api/client";
import { useAuth } from "../auth/useAuth";
import logoUrl from "../styles/catcsoft/assets/logo.svg";
import { IconLogin, IconShield } from "../components/icons";

interface LocationState {
  from?: string;
}

const DIMENSIONS = [
  "D1 Cobertura",
  "D2 Schema",
  "D3 i18n",
  "D4 Reglas",
  "D5 Test cases",
  "D6 Request",
  "D7 p95",
  "D8 Align",
];

const HERO_STATS = [
  { n: "0–100", l: "score multidimensional" },
  { n: "8", l: "dimensiones ponderadas" },
  { n: "D8", l: "alineación doc ↔ código" },
];

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user, loading } = useAuth();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Primer ingreso: canje de token de invitación → define clave.
  const [mode, setMode] = useState<"login" | "invite">("login");
  const [inviteToken, setInviteToken] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const handleRedeem = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/auth/redeem-invite", {
        token: inviteToken.trim(),
        new_password: newPassword,
      });
      // El endpoint deja la cookie de sesión: recargamos para hidratar el user.
      window.location.href = "/dashboard";
    } catch (err) {
      setError(
        axios.isAxiosError(err)
          ? err.response?.data?.detail || "No se pudo canjear el código"
          : "Error inesperado",
      );
      setSubmitting(false);
    }
  };

  if (!loading && user) {
    const dest = (location.state as LocationState | null)?.from || "/dashboard";
    navigate(dest, { replace: true });
    return null;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(username, password);
      const dest = (location.state as LocationState | null)?.from || "/dashboard";
      navigate(dest, { replace: true });
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.detail || "Error de autenticación");
      } else {
        setError("Error inesperado");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sar-login">
      {/* ---------- Panel izquierdo: brand + hero + dimensiones ---------- */}
      <div className="panel">
        <div className="row gap-3" style={{ alignItems: "center" }}>
          <img
            src={logoUrl}
            alt=""
            width={40}
            height={40}
            style={{ filter: "drop-shadow(0 2px 8px rgba(37,99,235,0.4))" }}
          />
          <div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 800,
                letterSpacing: "-0.02em",
                color: "#fff",
                lineHeight: 1.1,
              }}
            >
              Simappe
              <span style={{ fontWeight: 400, color: "#60a5fa" }}>
                {" "}
                API Runner
              </span>
            </div>
            <div
              style={{
                fontSize: 10.5,
                color: "#94a3b8",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                marginTop: 4,
                fontWeight: 600,
              }}
            >
              CatcSoft · Simappe Platform
            </div>
          </div>
        </div>

        <div style={{ maxWidth: 460 }}>
          <div
            className="sar-eyebrow"
            style={{ color: "#60a5fa", marginBottom: 14 }}
          >
            ▌ Validation runner v0.2
          </div>
          <h1
            style={{
              fontSize: 36,
              fontWeight: 700,
              letterSpacing: "-0.025em",
              lineHeight: 1.1,
              margin: 0,
              color: "#fff",
            }}
          >
            Valida tus Hojas Técnicas contra el{" "}
            <span
              style={{
                background:
                  "linear-gradient(135deg, #60a5fa 0%, #c4b5fd 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              código real
            </span>{" "}
            del pipeline HTU.
          </h1>
          <p
            style={{
              marginTop: 16,
              color: "#94a3b8",
              fontSize: 14,
              lineHeight: 1.6,
            }}
          >
            Score 0–100 multidimensional · 8 dimensiones ponderadas · cruce
            doc ↔ código contra el backend real. Multi-tenant via workspace.
          </p>
          <div
            className="row gap-2"
            style={{ marginTop: 22, flexWrap: "wrap" }}
          >
            {DIMENSIONS.map((d) => (
              <span
                key={d}
                className="mono"
                style={{
                  fontSize: 10.5,
                  color: "#cbd5e1",
                  border: "1px solid rgba(255,255,255,0.12)",
                  padding: "3px 7px",
                  borderRadius: 4,
                  background: "rgba(255,255,255,0.04)",
                }}
              >
                {d}
              </span>
            ))}
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 18,
            maxWidth: 460,
          }}
        >
          {HERO_STATS.map((s) => (
            <div key={s.l}>
              <div
                style={{
                  fontSize: 26,
                  fontWeight: 700,
                  letterSpacing: "-0.025em",
                  color: "#fff",
                  fontFamily: "var(--font-mono)",
                }}
              >
                {s.n}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "#64748b",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginTop: 2,
                }}
              >
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ---------- Form-side derecho ---------- */}
      <div className="form-side">
        <form
          onSubmit={mode === "invite" ? handleRedeem : handleSubmit}
          style={{ maxWidth: 360, width: "100%", margin: "0 auto" }}
        >
          <div className="sar-eyebrow" style={{ marginBottom: 8 }}>
            {mode === "invite" ? "Primer ingreso" : "Acceso seguro"}
          </div>
          <h2
            style={{
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: "-0.015em",
              margin: "0 0 6px",
            }}
          >
            {mode === "invite" ? "Activa tu cuenta" : "Inicia sesión"}
          </h2>
          <p
            className="dim"
            style={{ fontSize: 13, margin: "0 0 22px", color: "var(--sar-text-2)" }}
          >
            {mode === "invite"
              ? "Ingresa el código de invitación que te dio el administrador y define tu contraseña."
              : "Usa tu cuenta interna del runner."}
          </p>

          {mode === "invite" ? (
            <div style={{ display: "grid", gap: 12 }}>
              <div>
                <label style={{ fontSize: 11.5, fontWeight: 600, color: "var(--sar-text-2)", display: "block", marginBottom: 5 }}>
                  Código de invitación
                </label>
                <input
                  type="text" required autoFocus value={inviteToken}
                  onChange={(e) => setInviteToken(e.target.value)}
                  style={{ width: "100%", height: 36, padding: "0 12px", fontSize: 13, fontFamily: "var(--font-mono)", background: "var(--sar-surface)", color: "var(--sar-text)", border: "1px solid var(--sar-line)", borderRadius: 7, outline: "none" }}
                />
              </div>
              <div>
                <label style={{ fontSize: 11.5, fontWeight: 600, color: "var(--sar-text-2)", display: "block", marginBottom: 5 }}>
                  Nueva contraseña
                </label>
                <input
                  type="password" required minLength={8} autoComplete="new-password" value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={{ width: "100%", height: 36, padding: "0 12px", fontSize: 13, background: "var(--sar-surface)", color: "var(--sar-text)", border: "1px solid var(--sar-line)", borderRadius: 7, outline: "none" }}
                />
              </div>
              {error && (
                <div role="alert" style={{ padding: "10px 12px", background: "rgba(220, 38, 38, 0.08)", border: "1px solid rgba(220, 38, 38, 0.25)", borderRadius: 7, color: "#b91c1c", fontSize: 12.5 }}>{error}</div>
              )}
              <button type="submit" disabled={submitting} className="sar-btn gradient"
                style={{ height: 38, justifyContent: "center", fontSize: 13, fontWeight: 600, marginTop: 4, opacity: submitting ? 0.7 : 1, cursor: submitting ? "wait" : "pointer" }}>
                {submitting ? "Activando…" : "Activar cuenta"}
              </button>
              <button type="button" onClick={() => { setMode("login"); setError(null); }}
                style={{ background: "none", border: 0, color: "var(--sar-link)", fontSize: 12, cursor: "pointer", marginTop: 2 }}>
                ← Volver a iniciar sesión
              </button>
            </div>
          ) : (
          <div style={{ display: "grid", gap: 12 }}>
            <div>
              <label
                htmlFor="login-user"
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: "var(--sar-text-2)",
                  display: "block",
                  marginBottom: 5,
                }}
              >
                Usuario
              </label>
              <input
                id="login-user"
                type="text"
                required
                autoFocus
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                style={{
                  width: "100%",
                  height: 36,
                  padding: "0 12px",
                  fontSize: 13,
                  fontFamily: "inherit",
                  background: "var(--sar-surface)",
                  color: "var(--sar-text)",
                  border: "1px solid var(--sar-line)",
                  borderRadius: 7,
                  outline: "none",
                }}
              />
            </div>
            <div>
              <label
                htmlFor="login-pwd"
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: "var(--sar-text-2)",
                  display: "block",
                  marginBottom: 5,
                }}
              >
                Clave
              </label>
              <input
                id="login-pwd"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  width: "100%",
                  height: 36,
                  padding: "0 12px",
                  fontSize: 13,
                  fontFamily: "inherit",
                  background: "var(--sar-surface)",
                  color: "var(--sar-text)",
                  border: "1px solid var(--sar-line)",
                  borderRadius: 7,
                  outline: "none",
                }}
              />
            </div>

            {error && (
              <div
                role="alert"
                style={{
                  padding: "10px 12px",
                  background: "rgba(220, 38, 38, 0.08)",
                  border: "1px solid rgba(220, 38, 38, 0.25)",
                  borderRadius: 7,
                  color: "#b91c1c",
                  fontSize: 12.5,
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="sar-btn gradient"
              style={{
                height: 38,
                justifyContent: "center",
                fontSize: 13,
                fontWeight: 600,
                marginTop: 4,
                opacity: submitting ? 0.7 : 1,
                cursor: submitting ? "wait" : "pointer",
              }}
            >
              <IconLogin /> {submitting ? "Ingresando…" : "Entrar al runner"}
            </button>
            <button type="button" onClick={() => { setMode("invite"); setError(null); }}
              style={{ background: "none", border: 0, color: "var(--sar-link)", fontSize: 12, cursor: "pointer", marginTop: 2 }}>
              ¿Primer ingreso? Activa tu cuenta con el código de invitación
            </button>
          </div>
          )}

          <div
            className="row gap-2"
            style={{
              marginTop: 26,
              padding: "12px 14px",
              background: "var(--sar-surface-2)",
              borderRadius: 8,
              border: "1px solid var(--sar-line)",
            }}
          >
            <IconShield />
            <div
              style={{
                fontSize: 11.5,
                color: "var(--sar-text-2)",
                lineHeight: 1.5,
              }}
            >
              Acceso restringido por <b style={{ color: "var(--sar-text)" }}>VPN
              corporativa</b>. Tu IP queda registrada en el audit-log
              inmutable.
            </div>
          </div>

          <div
            className="row"
            style={{
              marginTop: 26,
              fontSize: 11,
              color: "var(--sar-text-3)",
              justifyContent: "space-between",
            }}
          >
            <span className="mono">v{__APP_VERSION__}</span>
            <span>© 2026 CatcSoft</span>
          </div>
        </form>
      </div>
    </div>
  );
}
